/**
 * Hook useReorderPipelinePhases - Reordena fases do pipeline via drag & drop
 * Versão: 1.0.0
 * Implementa optimistic update para UX fluida
 */
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { createLogger } from '@/lib/logger';
import { toast } from 'sonner';

// Logger para depuração
const logger = createLogger('ReorderPhases');

// Interface para ordem das fases
interface PhaseOrder {
  phase_id: string;
  sort_order: number;
}

/**
 * Hook para reordenar fases do pipeline
 * Usa função RPC segura com validação de tenant
 * 
 * @param pipelineId - ID do pipeline
 */
export function useReorderPipelinePhases(pipelineId: string) {
  const { currentTenant } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (phaseOrders: PhaseOrder[]) => {
      logger.debug('Reordenando fases:', phaseOrders);

      // Converte para JSON compatível com Supabase
      const jsonOrders = phaseOrders.map(p => ({
        phase_id: p.phase_id,
        sort_order: p.sort_order
      }));

      const { error } = await supabase.rpc('reorder_pipeline_phases', {
        p_phase_orders: jsonOrders as unknown as Parameters<typeof supabase.rpc<'reorder_pipeline_phases'>>[1]['p_phase_orders'],
        p_pipeline_id: pipelineId,
        p_tenant_hint: currentTenant?.id || null
      });

      if (error) {
        logger.error('Erro ao reordenar fases:', error);
        throw error;
      }

      logger.info('Fases reordenadas com sucesso');
    },
    onMutate: async (phaseOrders) => {
      // Cancela queries em andamento para evitar race conditions
      await queryClient.cancelQueries({ 
        queryKey: ['pipeline-phase-stats', pipelineId] 
      });

      // Snapshot do estado anterior para rollback
      const previousStats = queryClient.getQueryData(['pipeline-phase-stats', pipelineId]);

      // Optimistic update - reordena no cache local
      queryClient.setQueryData(
        ['pipeline-phase-stats', pipelineId],
        (old: any) => {
          if (!old) return old;
          
          // Cria mapa de nova ordem
          const orderMap = new Map(
            phaseOrders.map(p => [p.phase_id, p.sort_order])
          );
          
          // Aplica nova ordem
          const updated = [...old].sort((a: any, b: any) => {
            const orderA = orderMap.get(a.phase_id) ?? a.sort_order ?? 0;
            const orderB = orderMap.get(b.phase_id) ?? b.sort_order ?? 0;
            return orderA - orderB;
          });
          
          return updated;
        }
      );

      return { previousStats };
    },
    onError: (err, _, context) => {
      // Rollback em caso de erro
      if (context?.previousStats) {
        queryClient.setQueryData(
          ['pipeline-phase-stats', pipelineId],
          context.previousStats
        );
      }
      toast.error('Erro ao reordenar fases');
    },
    onSuccess: () => {
      // Invalida para garantir sincronização
      queryClient.invalidateQueries({ 
        queryKey: ['pipeline-phase-stats', pipelineId] 
      });
      toast.success('Fases reordenadas');
    }
  });
}
